import { clsx } from 'clsx';
import React from 'react';
import { FacilityContact } from '../../../../js/api';
import { Avatar } from '../../components/avatar';
import { getInitials } from '../../utils/utils';

interface FacilityContactCardProps {
  facilityContact: FacilityContact;
  onEdit?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  onDelete?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  roleMap?: Record<string, string>;
  canEdit?: boolean;
}

export const FacilityContactCard: React.FC<FacilityContactCardProps> = ({
  facilityContact,
  onEdit,
  onDelete,
  roleMap = {},
  canEdit = false,
}) => {
  const fullName =
    [facilityContact.contact.first_name, facilityContact.contact.last_name]
      .filter(Boolean)
      .join(' ') || 'Unnamed Contact';
  const initials = getInitials(fullName);
  const roleLabel = facilityContact.role
    ? roleMap[facilityContact.role] || facilityContact.role
    : null;

  return (
    <div className="tw:flex tw:flex-row tw:items-start tw:bg-zinc-100 tw:px-3 tw:py-3 tw:rounded-lg tw:gap-3 tw:dark:border tw:dark:border-white/10 tw:dark:bg-white/5 tw:dark:shadow-none">
      <Avatar
        initials={initials}
        className="tw:shrink-0 tw:size-9 tw:text-white tw:bg-brand-green-0"
      />

      {/* Stack on mobile, two equal columns on sm+ */}
      <div className="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:gap-1.5 tw:sm:flex-row tw:sm:gap-6 tw:sm:items-start">
        {/* Identity: name + role */}
        <div className="tw:flex tw:flex-col tw:min-w-0 tw:sm:flex-1">
          <span className="tw:text-sm tw:font-semibold tw:text-zinc-800 tw:dark:text-zinc-100 tw:leading-snug tw:break-words">
            {fullName}
          </span>
          <span className="tw:text-xs tw:xl:text-sm tw:text-zinc-500 tw:dark:text-zinc-400 tw:leading-snug tw:break-words">
            {roleLabel || <span className="tw:opacity-50">--</span>}
          </span>
        </div>

        {/* Contact methods: email + phone, stacked */}
        <div className="tw:flex tw:flex-col tw:gap-2 tw:min-w-0 tw:sm:flex-1">
          {facilityContact.contact.email ? (
            <a
              href={`mailto:${facilityContact.contact.email}`}
              className="tw:text-xs tw:xl:text-sm tw:text-zinc-700 tw:dark:text-zinc-200 tw:flex tw:items-center tw:!no-underline tw:min-w-0"
              title={facilityContact.contact.email}
            >
              <i
                className={clsx(
                  'fa-solid tw:mr-1.5 tw:text-zinc-400 tw:shrink-0 fa-lg',
                  facilityContact.receive_notifications
                    ? 'fa-envelope-circle-check tw:!text-brand-green-1'
                    : 'fa-envelope'
                )}
              />{' '}
              <span className="tw:truncate">{facilityContact.contact.email}</span>
            </a>
          ) : null}
          {facilityContact.contact.phone_number ? (
            <a
              href={`tel:${facilityContact.contact.phone_number}`}
              className="tw:text-xs tw:xl:text-sm tw:text-zinc-700 tw:dark:text-zinc-200 tw:flex tw:items-center tw:!no-underline"
              title={`Call ${facilityContact.contact.phone_number}`}
            >
              <i className="fa-solid fa-phone fa-lg tw:mr-1.5 tw:text-zinc-400 tw:dark:text-zinc-300 tw:shrink-0" />
              <span>
                {facilityContact.contact.phone_number}
                {facilityContact.contact.phone_number_extension &&
                  ` ext. ${facilityContact.contact.phone_number_extension}`}
              </span>
            </a>
          ) : null}
        </div>
      </div>

      {/* Actions: vertical on mobile, horizontal on sm+ */}
      {canEdit && (onEdit || onDelete) && (
        <div className="tw:shrink-0 tw:flex tw:flex-col tw:gap-3 tw:items-center tw:sm:flex-row tw:sm:gap-2">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:border-none tw:bg-transparent tw:p-0"
              aria-label="Edit Contact"
            >
              <i className="fa-regular fa-pen-to-square" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:border-none tw:bg-transparent tw:p-0"
              aria-label="Delete Contact"
            >
              <i className="fa-regular fa-trash" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
